import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  selectAllRecipes,
  selectAllIngredients,
  searchRecipes,
  SEARCH_MIN_QUERY_LENGTH,
  addSearchHistory,
  removeSearchHistory,
  clearSearchHistory,
  selectRecentSearches,
  useT,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";
import { Search as SearchIcon, X, Clock } from "react-feather";
import { RecipeList } from "../components/RecipeList";

const RECENT_LIMIT = 10;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function Search() {
  const t = useT();
  const dispatch = useAppDispatch();
  const [params, setParams] = useSearchParams();
  const qParam = params.get("query") ?? "";

  const [query, setQuery] = useState(qParam);
  // Keep the input in sync when the query arrives via the URL (header search /
  // shared link / recent chip) without clobbering local typing.
  useEffect(() => setQuery(qParam), [qParam]);

  const debounced = useDebounced(query, 150);

  const recipes = useAppSelector(selectAllRecipes);
  const ingredients = useAppSelector(selectAllIngredients);
  const selectRecent = useMemo(() => selectRecentSearches(RECENT_LIMIT), []);
  const recent = useAppSelector(selectRecent);

  const { results, matchedIngredients } = useMemo(
    () => searchRecipes(recipes, ingredients, debounced),
    [recipes, ingredients, debounced],
  );

  // Record a search only when explicitly submitted (Enter / recent chip).
  const submit = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setParams({ query: trimmed }, { replace: true });
    if (trimmed.length >= SEARCH_MIN_QUERY_LENGTH) {
      dispatch(
        addSearchHistory({
          query: trimmed,
          resultCount: searchRecipes(recipes, ingredients, trimmed).results.length,
        }),
      );
    }
  };

  const showRecent = query.trim() === "";
  const tooShort = debounced.trim().length < SEARCH_MIN_QUERY_LENGTH;

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-display text-3xl font-extrabold text-ink mb-5">{t("common.search")}</h1>

      <div className="flex items-center bg-surface border border-line rounded-xl px-4 py-3 gap-2.5 focus-within:border-terracotta transition-colors">
        <SearchIcon className="w-4 h-4 text-ink-faint shrink-0" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit(query)}
          placeholder={t("search.searchPlaceholder")}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-faint dark:placeholder:text-ink-faint"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="shrink-0 text-ink-faint hover:text-ink-soft cursor-pointer"
            aria-label={t("search.clear")}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showRecent ? (
        recent.length ? (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold text-ink-soft">{t("search.recentSearches")}</p>
              <button
                onClick={() => dispatch(clearSearchHistory())}
                className="text-sm font-medium text-terracotta hover:text-terracotta-dark cursor-pointer"
              >
                {t("search.clear")}
              </button>
            </div>
            {recent.map((item) => (
              <div key={item.id} className="flex items-center justify-between py-3 border-b border-line">
                <button
                  onClick={() => submit(item.query)}
                  className="flex-1 flex items-center gap-3 text-left cursor-pointer min-w-0"
                >
                  <Clock className="w-4 h-4 text-ink-faint shrink-0" />
                  <span className="text-sm text-ink truncate">{item.query}</span>
                </button>
                <button
                  onClick={() => dispatch(removeSearchHistory(item.id))}
                  className="pl-3 shrink-0 text-ink-faint hover:text-ink-soft cursor-pointer"
                  aria-label={t("common.remove")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-10 text-center text-ink-faint">
            {t("search.searchHint")}
          </p>
        )
      ) : tooShort ? (
        <p className="mt-10 text-center text-ink-faint">
          {t("search.tooShort", { count: SEARCH_MIN_QUERY_LENGTH })}
        </p>
      ) : results.length === 0 ? (
        <p className="mt-10 text-center text-ink-faint">{t("search.noResultsFor", { query: query.trim() })}</p>
      ) : (
        <div className="mt-6">
          <p className="text-sm text-ink-faint font-medium mb-2">
            {t("search.resultCount", { count: results.length })}
          </p>
          <RecipeList recipes={results} matchedIngredients={matchedIngredients} />
        </div>
      )}
    </div>
  );
}
